import { useMutation } from '@apollo/client';
import { useState } from 'react';

import ConfirmDialog from '@/componentsv2/ConfirmDialog';
import { firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { CLOSE_ENGAGEMENT } from './operations';

interface CloseEngagementDialogProps {
  engagementId: string;
  open: boolean;
  onCancel: () => void;
}

/**
 * The close confirmation, reached from the Hub's Settings tab (ENG-5688).
 *
 * It is a modal rather than the inline expander the pre-activation exits use.
 * Decline and withdraw end a request that never started; this ends work in
 * flight and is irreversible, so it interrupts rather than expands in place.
 *
 * The refusals arrive as coded errors and are rendered here rather than thrown
 * away, because each is recoverable by the person reading it: confirm or dispute
 * the outstanding hours, wait for the billing period to settle, or wait for the
 * hours to be invoiced. The dialog stays open on a refusal so the message sits
 * next to the action that produced it.
 */
export default function CloseEngagementDialog({
  engagementId,
  open,
  onCancel,
}: CloseEngagementDialogProps): JSX.Element {
  // refetchQueries, not just the normalized write: the mutation's own selection
  // set flips `status` and `viewerCanClose` on the cached entity, but it cannot
  // append to engagementHub's `activity` connection — so without this the
  // Overview tab's activity log silently omits the close the user just
  // performed, until a full reload. Same reason the hours and amendment
  // mutations refetch.
  const [closeEngagement, { loading }] = useMutation(CLOSE_ENGAGEMENT, {
    refetchQueries: ['engagementHub'],
  });
  const [error, setError] = useState<string | null>(null);

  function handleCancel() {
    // Dismissal is refused while the request is in flight: ConfirmDialog closes
    // on the backdrop and on escape as well as on Cancel, and a dialog that
    // vanishes mid-request reads as "canceled" for a close that is still going
    // to happen. The buttons below are disabled to make this guard visible
    // rather than a silent no-op.
    if (loading) return;
    setError(null);
    onCancel();
  }

  async function handleConfirm() {
    setError(null);
    try {
      await closeEngagement({ variables: { input: { engagementId } } });
      onCancel();
    } catch (err) {
      setError(
        getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? 'This engagement could not be closed.'
      );
    }
  }

  return (
    <ConfirmDialog
      open={open}
      dialogTitle="Close this engagement"
      message={
        <>
          <p>The engagement ends immediately and notifies the expert.</p>
          {error ? (
            <p role="alert" className="mt-12 text-red-600 body-2">
              {error}
            </p>
          ) : null}
        </>
      }
      confirmButtonLabel="Close engagement"
      confirmButtonProps={{ loading, disabled: loading }}
      cancelButtonLabel="Cancel"
      cancelButtonProps={{ disabled: loading }}
      onConfirm={() => void handleConfirm()}
      onCancel={handleCancel}
    />
  );
}
